import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { trackEvent } from '@/utils/analytics'
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import AuthLayout from '@/components/layout/AuthLayout'
import { useAppStore } from '@/store'
import { apiService } from '@/service/api'
import { markOnboardingCompleted } from '@/utils/onboarding'
import { markLandedOnFirstBoard } from '@/utils/firstBoardLanding'
import { useTranslation } from '@/i18n'
import { takePendingDestination } from '@/utils/pendingDestination'

/** Board library — the fallback when there is no board to land on. */
const DESTINATION = '/intelligent-board'
const BOARD_PATH = '/board'

// Sentinel value for the "Other" option. When selected on a step that allows it,
// a free-text field lets users self-describe (e.g. a civil engineer) — see the durable completion record
// and the Figma "Role - Other" state.
const OTHER = 'Other'

type Option = { id: string; value: string }

type Step = {
  /** Stable id used for i18n keys, analytics and selection state. */
  id: string
  /** Stable `question_key` of the seeded question row; sent in the submit payload. */
  questionKey: string
  prompt: string
  /** true = checkboxes (any number); false = single choice. */
  multi: boolean
  options: Option[]
  /** Whether selecting "Other" reveals a free-text field. */
  hasOther?: boolean
  otherPlaceholder?: string
}

// Order and content mirror the "Onboarding" screens in Figma (node 12545-1438):
// Industry → Role → Tools → Usage Intent → AI Experience → Attribution.
const STEPS: Step[] = [
  {
    id: 'industry',
    questionKey: 'industry',
    prompt: 'What industry do you work in?',
    multi: true,
    hasOther: true,
    otherPlaceholder: 'Tell us your industry',
    options: [
      { id: 'oil_gas', value: 'Oil & Gas' },
      { id: 'chemicals_petrochemicals', value: 'Chemicals & Petrochemicals' },
      { id: 'pharma_biotech', value: 'Pharmaceuticals & Biotech' },
      { id: 'food_beverage', value: 'Food & Beverage' },
      { id: 'mining_metals', value: 'Mining & Metals' },
      { id: 'energy_utilities', value: 'Energy & Utilities' },
      { id: 'pulp_paper_forest', value: 'Pulp, Paper & Forest Products' },
      { id: 'semiconductor_electronics', value: 'Semiconductor & Electronics' },
      { id: 'consulting_epc', value: 'Consulting & EPC' },
      { id: 'other', value: OTHER },
    ],
  },
  {
    id: 'role',
    questionKey: 'role',
    prompt: 'What best describes your role?',
    multi: true,
    hasOther: true,
    otherPlaceholder: 'Tell us your role',
    options: [
      { id: 'process_engineer', value: 'Process Engineer' },
      { id: 'project_design_engineer', value: 'Project / Design Engineer' },
      { id: 'controls_automation_engineer', value: 'Controls & Automation Engineer' },
      { id: 'reliability_maintenance_engineer', value: 'Reliability / Maintenance Engineer' },
      { id: 'rd_research_scientist', value: 'R&D / Research Scientist' },
      { id: 'plant_operations_manager', value: 'Plant / Operations Manager' },
      { id: 'engineering_manager_director', value: 'Engineering Manager / Director' },
      { id: 'vp_csuite', value: 'VP / C-Suite' },
      { id: 'student_academic', value: 'Student / Academic' },
      { id: 'other', value: OTHER },
    ],
  },
  {
    id: 'tools',
    questionKey: 'tools',
    prompt: 'What tools are in your current workflow?',
    multi: true,
    options: [
      { id: 'process_simulation_modeling', value: 'Process Simulation & Modeling' },
      { id: 'data_analytics', value: 'Data & Analytics' },
      { id: 'engineering_design', value: 'Engineering & Design' },
      { id: 'safety_compliance', value: 'Safety & Compliance' },
      { id: 'project_document_management', value: 'Project & Document Management' },
      { id: 'operational_mes_lims', value: 'Operational / MES / LIMS' },
    ],
  },
  {
    id: 'usage',
    questionKey: 'usage',
    prompt: 'How are you planning to use IntuigenceAI?',
    multi: false,
    options: [
      { id: 'just_me', value: 'Just me' },
      { id: 'department', value: 'For my department / business unit' },
      { id: 'org_wide', value: 'Evaluating for organization-wide deployment' },
      { id: 'academic', value: 'Academic / research use' },
    ],
  },
  {
    id: 'ai_experience',
    questionKey: 'ai_experience',
    prompt: 'How familiar are you with AI / ML tools in engineering?',
    multi: false,
    options: [
      { id: 'brand_new', value: 'Brand new' },
      { id: 'dabbled', value: 'Dabbled' },
      { id: 'comfortable', value: 'Comfortable' },
      { id: 'advanced', value: 'Advanced' },
    ],
  },
  {
    id: 'attribution',
    questionKey: 'attribution',
    prompt: 'How did you hear about IntuigenceAI?',
    multi: true,
    hasOther: true,
    otherPlaceholder: 'Tell us how',
    options: [
      { id: 'linkedin', value: 'LinkedIn' },
      { id: 'google_search', value: 'Google / Search' },
      { id: 'colleague_word_of_mouth', value: 'Colleague / Word of mouth' },
      { id: 'conference_event', value: 'Conference or event' },
      { id: 'aiche_professional_society', value: 'AIChE or professional society' },
      { id: 'podcast_youtube', value: 'Podcast or YouTube' },
      { id: 'university_professor', value: 'University / Professor' },
      { id: 'other', value: OTHER },
    ],
  },
]

type Phase = 'questions' | 'loading'

/**
 * Default workspace name used when we auto-create a tenant for a brand-new user
 * who arrived without an organization. They can rename it later in Settings.
 */
const defaultWorkspaceName = (name?: string, email?: string): string => {
  const owner = name?.trim() || email?.split('@')[0]?.trim()
  return owner ? `${owner}'s Workspace` : 'My Workspace'
}

export default function OnboardingV2() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, createOrganization, createBoard, generateDefaultBoardName } = useAppStore()

  const [stepIndex, setStepIndex] = useState(0)
  // Selected option values per step id.
  const [selections, setSelections] = useState<Record<string, string[]>>({})
  // Free-text "Other" value per step id.
  const [otherText, setOtherText] = useState<Record<string, string>>({})
  const [phase, setPhase] = useState<Phase>('questions')
  // Step ids the user has touched. The pre-fill below resolves asynchronously, so
  // a late response must not overwrite what was just picked — but only for the
  // steps actually edited: dropping the whole response would leave the other
  // steps looking blank while their saved answers were still in effect.
  const editedStepsRef = useRef<Set<string>>(new Set())

  const step = STEPS[stepIndex]
  const isLastStep = stepIndex === STEPS.length - 1
  const selected = selections[step.id] ?? []
  const otherSelected = !!step.hasOther && selected.includes(OTHER)

  const label = (key: string, defaultValue: string): string =>
    t(key, { defaultValue }) as string

  // Announce each step for funnel analytics.
  useEffect(() => {
    trackEvent('onboarding-step_viewed', { step_name: step.id, step_index: stepIndex })
  }, [step.id, stepIndex])

  // Pre-fill previous answers when the flow is re-entered, matching the legacy
  // flow (its dynamic-questions screen pre-populated the same way). /onboarding
  // stays reachable at any time, so a revisit must show what you picked rather
  // than a blank wizard — and re-submitting then updates the answers.
  useEffect(() => {
    let cancelled = false
    const loadExisting = async (): Promise<void> => {
      if (!user?.userId || !user?.tenant_id) return
      try {
        const { answers } = await apiService.getMyOnboardingAnswers()
        if (cancelled || answers.length === 0) return

        const byKey = new Map(answers.map((a) => [a.question_key, a.answer_value]))
        const restoredSelections: Record<string, string[]> = {}
        const restoredOther: Record<string, string> = {}

        for (const s of STEPS) {
          const saved = byKey.get(s.questionKey)
          if (saved === undefined) continue
          const values = (Array.isArray(saved) ? saved : [saved]).map(String)
          for (const value of values) {
            // Answers store stable option ids; the wizard's state is keyed by
            // display label. An id we don't recognise is the user's own "Other"
            // text, so restore it into the free-text field.
            const option = s.options.find((o) => o.id === value)
            if (option) {
              restoredSelections[s.id] = [...(restoredSelections[s.id] ?? []), option.value]
            } else if (s.hasOther) {
              restoredSelections[s.id] = [...(restoredSelections[s.id] ?? []), OTHER]
              restoredOther[s.id] = value
            }
          }
        }

        // Merge per step, leaving anything the user has already touched alone.
        const keep = (stepId: string): boolean => !editedStepsRef.current.has(stepId)
        setSelections((prev) => {
          const merged = { ...prev }
          for (const [stepId, values] of Object.entries(restoredSelections)) {
            if (keep(stepId)) merged[stepId] = values
          }
          return merged
        })
        setOtherText((prev) => {
          const merged = { ...prev }
          for (const [stepId, text] of Object.entries(restoredOther)) {
            if (keep(stepId)) merged[stepId] = text
          }
          return merged
        })
      } catch (error) {
        // Non-blocking: a failed pre-fill just means an empty wizard.
        console.error('Failed to load previous onboarding answers', error)
      }
    }
    void loadExisting()
    return () => {
      cancelled = true
    }
  }, [user?.userId, user?.tenant_id])

  const toggleOption = (value: string) => {
    editedStepsRef.current.add(step.id)
    setSelections((prev) => {
      const current = prev[step.id] ?? []
      let next: string[]
      if (step.multi) {
        next = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value]
      } else {
        // Single choice: clicking the selected option clears it, else replaces.
        next = current.includes(value) ? [] : [value]
      }
      return { ...prev, [step.id]: next }
    })
  }

  const goToNextStep = () => {
    if (isLastStep) {
      void finishOnboarding()
    } else {
      setStepIndex((i) => i + 1)
    }
  }

  const handleBack = () => setStepIndex((i) => Math.max(0, i - 1))

  const handleContinue = () => {
    goToNextStep()
  }

  const handleSkip = () => {
    trackEvent('onboarding-skipped', { step_name: step.id })
    goToNextStep()
  }

  // Selection state carries display labels (what the option renders as), but an
  // answer is SAVED as the option's stable id: the labels are i18n'd, so keying
  // stored answers on them would break every consumer the first time copy
  // changes. "Other" has no meaningful id, so it carries what the user typed.
  const resolveValues = (s: Step, values: string[]): string[] =>
    values.map((v) => {
      if (v === OTHER) {
        const typed = otherText[s.id]?.trim()
        if (typed) return typed
      }
      return s.options.find((o) => o.value === v)?.id ?? v
    })

  /**
   * Where to drop the user now that onboarding is done: straight into a board
   * rather than the library.
   *
   * A brand-new workspace has no boards, so one is created here — the only place
   * that happens automatically. An invited user who already has boards gets their
   * most recent one (the list comes back `updatedAt DESC`).
   *
   * Any failure falls back to the library, which is where this flow used to land
   * unconditionally — a board we could not create or find must never trap the
   * user on a blank screen.
   */
  const resolveLandingRoute = async (): Promise<string> => {
    try {
      // FS-663: a user who arrived via a share link finishes onboarding ON that
      // resource. Checked FIRST — an invitee joins an org that usually already
      // has boards, so the `existing[0]` branch below would otherwise drop them
      // on a colleague's most-recent board instead of the one shared with them.
      // TAKE, not peek: onboarding is the terminal step of the invited-recipient
      // flow, so the destination must not survive to hijack their next login.
      const pending = takePendingDestination()
      if (pending) {
        trackEvent('onboarding-landed_on_shared_resource', { path: pending })
        return pending
      }

      // No local destination — ASK THE SERVER what was shared with us.
      //
      // This is the ONLY thing that works for the common invited-recipient path:
      // Keycloak's invitation email links to the bare app root, so the invitee
      // never visits the shared deep link in this browser and there is nothing
      // in localStorage to resume. Without this they land on a colleague's
      // most-recent board and the invite appears to have done nothing.
      //
      // With SEVERAL pending shares we take the NEWEST (the server orders by
      // created_at DESC). It is a proxy, not a certainty: Keycloak's invitation
      // is per-ORGANISATION, not per-resource, so accepting it carries no signal
      // about which board the invite was for — every pending share becomes
      // reachable at once. The newest is overwhelmingly the one just sent, and
      // landing on a shared board beats landing on a stranger's.
      //
      // The EXACT answer is the other path: our own share email carries the deep
      // link, so clicking it sets the pending destination consumed above. That
      // requires SMTP to be configured.
      try {
        const { shares } = await apiService.getMyPendingShares()
        const newest = shares.find((s) => Boolean(s.resource_path))
        if (newest?.resource_path) {
          trackEvent('onboarding-landed_on_shared_resource', {
            path: newest.resource_path,
            share_count: shares.length,
          })
          return newest.resource_path
        }
      } catch {
        // Never block onboarding on this — fall through to the normal landing.
      }

      // Asked directly rather than through `loadBoardList`, which swallows its
      // own error and leaves the list empty — indistinguishable from "no boards",
      // which would have us create a second board for someone who has plenty.
      // Only ever asks "does this user have a board yet?" and reads `[0]`, so one
      // row answers it. Unpaged, this was the 43 MB fetch (FS-696) on a screen that
      // needed a single boolean.
      const { items: existing } = await apiService.listWorkspaces({ pageNumber: 0, pageSize: 1 })
      if (existing.length > 0) return `${BOARD_PATH}/${existing[0].id}`

      const created = await createBoard(generateDefaultBoardName())
      if (!created) return DESTINATION

      trackEvent('onboarding-first_board_created', { board_id: created.id })
      return `${BOARD_PATH}/${created.id}`
    } catch (error) {
      console.error('Failed to prepare the first board', error)
      return DESTINATION
    } finally {
      // Either way this counts as the one-time landing, so a later login shows
      // the library instead of jumping into a board again.
      markLandedOnFirstBoard(user?.userId)
    }
  }

  const finishOnboarding = async () => {
    const answers = STEPS.map((s) => {
      const values = selections[s.id] ?? []
      if (values.length === 0) return null
      return { questionKey: s.questionKey, answerValue: resolveValues(s, values) }
    }).filter((a): a is { questionKey: string; answerValue: string[] } => a !== null)

    trackEvent('onboarding-questions_completed', {
      question_count: STEPS.length,
      answers_provided: answers.length,
    })
    setPhase('loading')

    // A user who arrived without an organization (e.g. fresh sign-up) has no
    // tenant, so every tenant-scoped request 400s ("no tenant association")
    // until one exists. Auto-create a default workspace before we let them into
    // the app. Invited users already belong to the inviter's org (they have a
    // tenant_id) — for them we skip creation and only save their answers.
    if (!user?.tenant_id) {
      try {
        // createOrganization refreshes the session so the new org claim lands
        // on the token before any tenant-scoped request fires.
        await createOrganization(defaultWorkspaceName(user?.name, user?.email))
      } catch {
        // createOrganization already surfaces a toast. Without a tenant the app
        // is unusable, so don't mark complete or navigate — return to the
        // questions so the user can retry finishing.
        setPhase('questions')
        return
      }
    }

    try {
      // Save preferences best-effort — must never block finishing onboarding.
      // Logged rather than swallowed silently: an invisible failure here is what
      // hid the answers never persisting at all.
      if (user?.userId && answers.length > 0) {
        await apiService.submitOnboardingAnswers({ answers }).catch((error: unknown) => {
          console.error('Failed to save onboarding answers', error)
        })
      }
    } catch {
      // Non-blocking — continue even if save fails.
    }

    // Persist a durable, server-side completion record so onboarding survives a
    // cache/localStorage clear, a new browser, or a new device . Runs
    // after createOrganization, so the request carries the refreshed token (and
    // tenant). Best-effort: a failure only means the user may re-onboard after a
    // future cache clear; the local flag below still lets them through now.
    try {
      await apiService.completeOnboarding()
    } catch {
      // Non-blocking — the localStorage flag is the fallback.
    }

    // Record completion locally before navigating so the route guard won't
    // bounce the user back to /onboarding . localStorage is the
    // synchronous fast-path cache for the durable server record above.
    markOnboardingCompleted(user?.userId)
    navigate(await resolveLandingRoute(), { replace: true })
  }

  if (phase === 'loading') {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center justify-center gap-6 min-h-[300px]">
          <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-dark-text mb-2">
              {t('onboarding:setup.title', { defaultValue: 'Setting up your workspace' })}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-[300px]">
              {t('onboarding:setup.description', {
                defaultValue:
                  'This only takes a few seconds while we configure Intuigence AI for your role.',
              })}
            </p>
          </div>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="flex flex-col gap-6 w-full max-w-[400px]">
        {stepIndex > 0 && (
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1 self-start text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-dark-text transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {label('onboarding:nav.back', 'Back')}
          </button>
        )}

        <h2 className="text-2xl font-semibold text-gray-900 dark:text-dark-text">
          {label(`onboarding:steps.${step.id}.prompt`, step.prompt)}
        </h2>

        <div className="flex flex-col gap-3">
          {step.options.map((opt) => {
            const isSelected = selected.includes(opt.value)
            return (
              <label
                key={opt.id}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${
                  isSelected
                    ? 'border-gray-900 dark:border-white bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-500 text-gray-900 dark:text-dark-text'
                }`}
              >
                <input
                  type={step.multi ? 'checkbox' : 'radio'}
                  name={`onboarding-${step.id}`}
                  checked={isSelected}
                  onChange={() => toggleOption(opt.value)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 flex items-center justify-center shrink-0 border-2 ${
                    step.multi ? 'rounded' : 'rounded-full'
                  } ${
                    isSelected
                      ? 'bg-white dark:bg-gray-900 border-white dark:border-gray-900'
                      : 'border-gray-300 dark:border-gray-500'
                  }`}
                >
                  {isSelected && (
                    <svg className="w-3 h-3 text-gray-900 dark:text-white" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <span className="text-sm font-medium">
                  {label(`onboarding:steps.${step.id}.options.${opt.id}`, opt.value)}
                </span>
              </label>
            )
          })}
        </div>

        {otherSelected && (
          <Input
            type="text"
            value={otherText[step.id] ?? ''}
            onChange={(e) => {
              editedStepsRef.current.add(step.id)
              setOtherText((prev) => ({ ...prev, [step.id]: e.target.value }))
            }}
            placeholder={label(
              `onboarding:steps.${step.id}.other_placeholder`,
              step.otherPlaceholder ?? 'Tell us more',
            )}
            aria-label={label(
              `onboarding:steps.${step.id}.other_placeholder`,
              step.otherPlaceholder ?? 'Tell us more',
            )}
            autoFocus
          />
        )}

        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleSkip} className="px-6">
            {label('onboarding:nav.skip', 'Skip this step')}
          </Button>
          <Button
            onClick={handleContinue}
            disabled={selected.length === 0}
            className="px-6 bg-orange-500 hover:bg-orange-600 text-white"
          >
            {label('onboarding:nav.continue', 'Continue')}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </AuthLayout>
  )
}
